// 推廣歸因參數的對外入口 —— TI-15642 / JT-1785738540。
// 規格：spec/JT-1785738540_referral-param-persistence-fallback.md
//
// 所有讀取點（註冊、第三方登入、活動、綁定銀行、Umami identify、App 下載連結…）一律經由本檔的
// resolveXxx()，取代改版前散落各處的 storage.get('ac') 直讀——只要漏掉一處就會行為漂移。
//
// ⚠️ 函式刻意加 Referral 前綴：utils/conversionPayload.js 已有一支語意不同的 resolveAc()（S2S 轉換管線，
//    30 天 conv_ac、無值時落 DEFAULT_AC），而 Nuxt 會自動匯入 app/utils/**，同名會互相覆蓋且只留一則 WARN。
import * as Sentry from '@sentry/nuxt'

import {
  AC_KEYS,
  FC_KEYS,
  KPIX_KEYS,
  PIX_KEYS,
  TTPIX_KEYS,
  normalizeReferral,
  persistFailed,
  persistReferral,
  readReferral,
} from '@/utils/referralParams'
import { createReferralIo } from '@/utils/referralStorage'

// 擷取清單：00.query.client.js 與 router.afterEach 都走這一份，新增參數只需改這裡
export const CAPTURE_TARGETS = [
  { name: 'ac', keys: AC_KEYS },
  { name: 'fc', keys: FC_KEYS },
  { name: 'pix', keys: PIX_KEYS },
  { name: 'ttpix', keys: TTPIX_KEYS },
  { name: 'kpix', keys: KPIX_KEYS },
]

// 去重：同 session、同 key、同失敗態只送一次，避免 afterEach 每次導航洗版
const reported = new Set()

const notify = (name, result) => {
  if (!persistFailed(result)) return
  if (typeof window === 'undefined') return

  const fingerprint = `${name}:${result.local}:${result.cookie}`
  if (reported.has(fingerprint)) return
  reported.add(fingerprint)

  // 刻意不把參數值放進訊息主體：高基數且無必要
  Sentry.captureMessage(`[Referral] persist failed: ${name}`, {
    level: 'warning',
    tags: {
      feature: 'url-param-fallback',
      param_key: name,
      storage_medium: result.local !== 'ok' && result.cookie !== 'ok' ? 'both' : result.local !== 'ok' ? 'localStorage' : 'cookie',
      local_result: result.local,
      cookie_result: result.cookie,
    },
  })
}

export const readReferralValue = (keys, search) => readReferral(createReferralIo(search), keys)

export const persistReferralValue = (name, value, options, search) => {
  const result = persistReferral(name, value, createReferralIo(search), options)
  notify(name, result)
  return result
}

// 使用者在代理碼欄位做過的決定（含刻意留白）。undefined ＝ 這一輪還沒做過決定。
// ⚠️ 只在瀏覽器端寫入與讀取：本檔會被 SSR 一併載入，模組層狀態在伺服器是跨請求共用的。
let explicitAc

// 這一輪已擷取到的網址 ac，用來判斷「網址是不是帶了新的 last-click」（見 startNewRoundIfNewLastClick）
let capturedUrlAc

// 三層解析（網址 → localStorage → cookie）。SSR 階段三層皆取不到，回 null，與改版前 storage.get 一致。
// ⚠️ 使用者的明確決定優先於網址：`?ac=meta2` 仍留在網址列時，若不讓 explicitAc 勝出，使用者剛改成的
//    other99（或剛清空的空值）會在下一次解析被網址值蓋回去——第三方登入 redirect 與 Telegram 登入
//    都是在使用者按下確認之後才解析，正是踩這個坑的地方。
export const resolveReferralAc = (search) => {
  if (typeof window !== 'undefined' && explicitAc !== undefined) return explicitAc
  return readReferralValue(AC_KEYS, search).value
}
export const resolveReferralFc = (search) => readReferralValue(FC_KEYS, search).value
export const resolveReferralPix = (search) => readReferralValue(PIX_KEYS, search).value
export const resolveReferralKpix = (search) => readReferralValue(KPIX_KEYS, search).value
export const resolveReferralTtpix = (search) => readReferralValue(TTPIX_KEYS, search).value

// 使用者在代理碼欄位的決定（含刻意留白）優先於網址／儲存這類環境值：
// 有輸入 → 以輸入值覆寫兩個媒介；清空 → 兩個媒介一起清除，下次不得再預填舊值。
export const persistAffiliateCode = (value) => {
  const result = persistReferralValue('ac', value, { allowClear: true })
  // 清空時記成 null（＝有決定、但決定是「不帶」），與「尚未做決定」的 undefined 區分
  if (typeof window !== 'undefined') explicitAc = normalizeReferral(value)
  return result
}

// 僅供測試與「使用者尚未做決定」的重置情境使用：把這一輪的狀態整組歸零
export const clearExplicitAffiliateCode = () => {
  explicitAc = undefined
  capturedUrlAc = undefined
}

// 網址帶了「與這一輪已擷取值不同」的 ac ＝ 新的一次 last-click，使用者上一輪的決定就此失效
// （否則已開啟的 PWA 殼接到新代理連結時，所有讀取點都還停在舊決定上）。
// ⚠️ 判定條件不可放寬成「網址有 ac 就重置」：`?ac=meta2` 會一路留在網址列，每次 router.afterEach
//    都會把使用者剛改成的 other99（或剛清空的留白）打回網址值，正好打破 REQ-16 要守的東西。
const startNewRoundIfNewLastClick = (found) => {
  if (typeof window === 'undefined') return
  if (!found.from.startsWith('url:')) return
  if (found.value === capturedUrlAc) return
  capturedUrlAc = found.value
  explicitAc = undefined
}

export const captureReferralParams = (search) => {
  CAPTURE_TARGETS.forEach(({ name, keys }) => {
    const found = readReferralValue(keys, search)
    // 三層皆空 → 略過，不以空值覆蓋既有副本
    if (!found.value) return
    if (name === 'ac') startNewRoundIfNewLastClick(found)
    persistReferralValue(name, found.value, undefined, search)
  })
}
